import React, { FunctionComponent, Fragment, useMemo, useState, useCallback } from 'react';
import { Grid, Typography, ExpandMoreIcon, NavigateNextIcon, makeStyles } from '@rbx/ui';
import StandardMarkdownNoMargin from '@modules/ui/markdown/components/StandardMarkdownNoMargin';
import { useTranslation } from '@rbx/intl';
import { EBasicSchemaFormat } from '@rbx/docsite-utilities/dist/browser';
import { CloudTag } from '@modules/ui/tags/types';
import ReferenceMemberTags from '@modules/apiReference/page/sharedUI/components/ReferenceMemberTags';
import { TSchemaItem } from './SchemasProvider';
import { useResource } from './ResourceProvider';
import OasType from '../../../sharedUI/components/OasType';
import SchemaTable from './SchemaTable';

type useStylesProps = { indent: number };

const getDescriptionForOpenCloudFormat = (
  translate: ReturnType<typeof useTranslation>['translate'],
  givenDescription?: string,
  givenFormat?: string,
) => {
  if (!givenDescription) {
    return givenDescription;
  }

  let descriptionForFormat = givenDescription;
  if (!givenDescription.endsWith('.')) {
    descriptionForFormat = `${givenDescription}.`;
  }
  if (givenFormat === EBasicSchemaFormat.FieldMask) {
    descriptionForFormat += ` ${translate('Label.FieldMaskDescription')}`;
  }
  if (givenFormat === EBasicSchemaFormat.DateTime) {
    descriptionForFormat += ` ${translate('Label.TimestampDescription')}`;
  }
  if (givenFormat === EBasicSchemaFormat.Enum) {
    descriptionForFormat += ` ${translate('Label.PossibleValues')}`;
  }
  return descriptionForFormat;
};

const useStyles = makeStyles<useStylesProps>()((theme, { indent }) => ({
  container: {
    padding: 16,
    paddingLeft: indent * 8,
    borderBottom: `solid 1px ${theme.palette.components.divider}`,
    '&:last-of-type': {
      borderBottom: 'none',
    },
    width: '100%',
    position: 'relative',
    '& .MuiTypography-root': {
      fontSize: 16,
      '& .inline-code': {
        fontSize: 14,
      },
    },
  },
  name: {
    marginRight: 8,
  },
  schemaDescription: {
    width: '100%',
    marginTop: theme.spacing(2),
    '& ul': {
      paddingLeft: 24,
      paddingRight: 24,
    },
    '& table': {
      backgroundColor: theme.palette.surface[100],
      padding: theme.spacing(1),
      marginBottom: `8px !important`,
      '& tr:last-of-type > td': {
        borderBottom: 'none',
      },
    },
  },
  bottomDescriptionWrapper: { marginTop: 4, width: '100%', paddingLeft: 24 },
  icon: {
    cursor: 'pointer',
    marginLeft: -24,
    marginTop: 2,
    position: 'absolute',
    width: 21,
    height: 21,
  },
  tags: {
    display: 'inline-block',
    marginTop: 0,
    marginLeft: 12,
    [theme.breakpoints.down('Medium')]: {
      marginTop: theme.spacing(1),
      display: 'block',
      marginLeft: 0,
    },
  },
  tag: {
    textTransform: 'uppercase',
    marginLeft: 12,
    marginTop: 0,
    '&.MuiTypography-root': {
      fontSize: 12,
    },
  },
  title: {
    marginRight: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
  },
}));

export type SchemaTableRowProps = {
  indent?: number;
  bottomDescription?: React.ReactNode;
  schemaItem: TSchemaItem;
};

const ToggleExpandIcon = ({
  className,
  expanded,
  onClick,
}: {
  className: string;
  expanded: boolean;
  onClick: () => void;
}) => {
  if (expanded) {
    return <ExpandMoreIcon onClick={onClick} className={className} />;
  }
  return <NavigateNextIcon onClick={onClick} className={className} />;
};

const SchemaTableRow: FunctionComponent<React.PropsWithChildren<SchemaTableRowProps>> = ({
  schemaItem,
  indent = 0,
  bottomDescription,
}) => {
  const {
    name,
    groupType,
    type,
    format,
    refName,
    required,
    readOnly,
    writeOnly,
    immutable,
    description,
  } = schemaItem ?? {};
  const [expanded, setExpanded] = useState(false);
  const {
    classes: {
      container,
      name: nameStyle,
      icon: iconStyle,
      schemaDescription,
      tags: tagsStyle,
      title: titleStyle,
      bottomDescriptionWrapper,
    },
  } = useStyles({ indent });
  const { translate } = useTranslation();
  const { refName: resourceRefName } = useResource();
  // dont show the expand icon if the refName is a Resource
  const expandable = useMemo(
    () => refName && resourceRefName !== refName,
    [refName, resourceRefName],
  );

  const tags: CloudTag[] = useMemo(
    () =>
      [
        required && CloudTag.Required,
        immutable && CloudTag.Immutable,
        readOnly && CloudTag.OutputOnly,
        writeOnly && CloudTag.InputOnly,
      ].filter((x) => !!x) as CloudTag[],
    [required, immutable, readOnly, writeOnly],
  );

  const descriptionForFormat = useMemo(
    () => getDescriptionForOpenCloudFormat(translate, description, format),
    [description, format, translate],
  );
  const onToggleExpand = useCallback(() => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
  }, [expanded]);

  return (
    <Fragment>
      <article className={container}>
        <Grid container direction='row' alignItems='center' className={titleStyle}>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
          <header role='button' onClick={onToggleExpand} tabIndex={0}>
            {expandable && (
              <ToggleExpandIcon
                className={iconStyle}
                expanded={expanded}
                onClick={onToggleExpand}
              />
            )}
            <Typography className={nameStyle} color='primary' variant='body2'>
              {name}
            </Typography>
            <OasType
              typeName={type}
              refName={refName}
              format={format}
              groupTypeName={groupType}
              // if the refName is Resource, show as it is
              // otherwise show as 'object'
              showObjectForReference={!(refName && resourceRefName === refName)}
            />
            <ReferenceMemberTags className={tagsStyle} tags={tags} referenceClass='cloud' />
          </header>
        </Grid>
        {descriptionForFormat && (
          <div className={schemaDescription}>
            <StandardMarkdownNoMargin>{descriptionForFormat}</StandardMarkdownNoMargin>
          </div>
        )}
        {bottomDescription && <div className={bottomDescriptionWrapper}>{bottomDescription}</div>}
      </article>
      {expanded && refName && <SchemaTable indent={indent + 1} refName={refName} />}
    </Fragment>
  );
};

export default SchemaTableRow;
